import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  Clock,
  Calendar,
  Coffee,
  Check,
  Send,
  Home
} from 'lucide-react';

const MobileTimesheet = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editForm, setEditForm] = useState({
    start_time: '',
    finish_time: '',
    lunch_break_minutes: 30,
    notes: ''
  });

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    fetchTimesheet();
  }, []);

  const fetchTimesheet = async (weekStart = null) => {
    setLoading(true);
    try {
      const url = weekStart 
        ? `/api/mobile/timesheets?week_start=${weekStart}`
        : '/api/mobile/timesheets';
      const response = await api.get(url);
      setWeekData(response.data);
      
      // Auto-select today if in current week
      const today = new Date().toISOString().split('T')[0];
      const todayEntry = response.data.entries.find(e => e.date === today);
      if (todayEntry) {
        selectDay(todayEntry);
      } else if (response.data.entries.length > 0) {
        selectDay(response.data.entries[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch timesheet');
    } finally {
      setLoading(false);
    }
  };

  const selectDay = (entry) => {
    setSelectedDay(entry);
    setEditForm({
      start_time: entry.start_time || '',
      finish_time: entry.finish_time || '',
      lunch_break_minutes: entry.lunch_break_minutes || 30,
      notes: entry.notes || ''
    });
  };

  const navigateWeek = (direction) => {
    if (!weekData) return;
    const currentStart = new Date(weekData.week_start);
    currentStart.setDate(currentStart.getDate() + (direction * 7));
    fetchTimesheet(currentStart.toISOString().split('T')[0]);
  };

  const handleSave = async () => {
    if (!selectedDay) return;
    
    if (editForm.start_time && editForm.finish_time && editForm.start_time >= editForm.finish_time) {
      toast.error('Finish time must be after start time');
      return;
    }

    setSaving(true);
    try {
      await api.post('/api/mobile/timesheets', {
        date: selectedDay.date,
        start_time: editForm.start_time || null,
        finish_time: editForm.finish_time || null,
        lunch_break_minutes: editForm.lunch_break_minutes,
        notes: editForm.notes || null
      });
      toast.success('Timesheet saved');
      fetchTimesheet(weekData.week_start);
    } catch (error) {
      toast.error('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitWeek = async () => {
    if (!weekData) return;
    
    try {
      await api.post(`/api/mobile/timesheets/submit-week?week_start=${weekData.week_start}`);
      toast.success('Timesheet submitted for approval');
      fetchTimesheet(weekData.week_start);
    } catch (error) {
      toast.error('Failed to submit timesheet');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.getDate();
  };

  const formatWeekRange = () => {
    if (!weekData) return '';
    const start = new Date(weekData.week_start);
    const end = new Date(weekData.week_end);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-AU', options)} - ${end.toLocaleDateString('en-AU', options)}`;
  };

  const calculateHours = () => {
    if (!editForm.start_time || !editForm.finish_time) return 0;
    const start = new Date(`2000-01-01T${editForm.start_time}`);
    const finish = new Date(`2000-01-01T${editForm.finish_time}`);
    const diff = (finish - start) / 1000 / 60; // minutes
    return Math.max(0, (diff - editForm.lunch_break_minutes) / 60).toFixed(1);
  };

  const getTotalWeekHours = () => {
    if (!weekData) return 0;
    return weekData.entries.reduce((sum, e) => sum + (e.total_hours || 0), 0).toFixed(1);
  };

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  const getEntryStatus = (entry) => {
    if (entry.status === 'submitted') return 'submitted';
    if (entry.status === 'approved') return 'approved';
    if (entry.total_hours > 0) return 'filled';
    return 'empty';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] pb-24">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/dashboard')} className="text-gray-400 p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-white font-bold flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#ffd108]" />
                Timesheet
              </h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')}
            className="text-gray-400 p-2"
            data-testid="home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="px-4 py-3 bg-[#252525] flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)} className="text-gray-400">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#ffd108]" />
          <span className="text-white font-medium">{formatWeekRange()}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)} className="text-gray-400">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Week Summary */}
      <div className="px-4 py-3 bg-[#2a2a2a] flex items-center justify-between">
        <span className="text-gray-400 text-sm">Total Hours</span>
        <span className="text-[#ffd108] font-bold text-xl">{getTotalWeekHours()} hrs</span>
      </div>

      {/* Day Selector */}
      <div className="px-4 py-3">
        <div className="grid grid-cols-7 gap-1">
          {weekData?.entries.map((entry, idx) => {
            const status = getEntryStatus(entry);
            return (
              <button
                key={entry.date}
                onClick={() => selectDay(entry)}
                className={`p-2 rounded-lg text-center transition-colors ${
                  selectedDay?.date === entry.date
                    ? 'bg-[#ffd108] text-[#1a1a1a]'
                    : 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a]'
                }`}
              >
                <div className="text-xs font-medium">{dayNames[idx]}</div>
                <div className={`text-lg font-bold ${isToday(entry.date) ? 'underline' : ''}`}>
                  {formatDate(entry.date)}
                </div>
                <div className="h-1.5 w-1.5 rounded-full mx-auto mt-1" style={{
                  backgroundColor: 
                    status === 'approved' ? '#22c55e' :
                    status === 'submitted' ? '#f59e0b' :
                    status === 'filled' ? '#3b82f6' : 'transparent'
                }} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {selectedDay && (
          <>
            {/* Time Entry */}
            <Card className="bg-[#2a2a2a] border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center justify-between">
                  <span>{new Date(selectedDay.date).toLocaleDateString('en-AU', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                  {selectedDay.status === 'submitted' && (
                    <Badge className="bg-amber-900 text-amber-300">Submitted</Badge>
                  )}
                  {selectedDay.status === 'approved' && (
                    <Badge className="bg-green-900 text-green-300">Approved</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400 text-xs">Start Time</Label>
                    <Input
                      type="time"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm({...editForm, start_time: e.target.value})}
                      className="bg-[#1a1a1a] border-gray-600 text-white mt-1"
                      disabled={selectedDay.status !== 'draft'}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Finish Time</Label>
                    <Input
                      type="time"
                      value={editForm.finish_time}
                      onChange={(e) => setEditForm({...editForm, finish_time: e.target.value})}
                      className="bg-[#1a1a1a] border-gray-600 text-white mt-1"
                      disabled={selectedDay.status !== 'draft'}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-400 text-xs flex items-center gap-2">
                    <Coffee className="h-3 w-3" />
                    Lunch Break
                  </Label>
                  <Select
                    value={String(editForm.lunch_break_minutes)}
                    onValueChange={(v) => setEditForm({...editForm, lunch_break_minutes: parseInt(v)})}
                    disabled={selectedDay.status !== 'draft'}
                  >
                    <SelectTrigger className="bg-[#1a1a1a] border-gray-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Break</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Calculated Hours */}
                <div className="bg-[#1a1a1a] rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-xs mb-1">Total Hours</p>
                  <p className="text-[#ffd108] text-3xl font-bold">{calculateHours()}</p>
                </div>

                {selectedDay.status === 'draft' && (
                  <Button 
                    className="w-full btn-volt" 
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Save Entry
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span>Saved</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                <span>Submitted</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span>Approved</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#2a2a2a] border-t border-gray-700 p-4">
        <Button 
          variant="outline" 
          className="w-full border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108] hover:text-[#1a1a1a]"
          onClick={handleSubmitWeek}
        >
          <Send className="h-4 w-4 mr-2" />
          Submit Week for Approval
        </Button>
      </div>
    </div>
  );
};

export default MobileTimesheet;
